'use client';

import React from 'react';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram } from 'lucide-react';
import { useTheme } from '@/providers/ThemeProvider';
import { useBasePath } from '@/providers/BasePathProvider';
import Link from 'next/link';

export function Footer() {
  const { config } = useTheme();
  const { buildPath } = useBasePath();

  const footerLinks = {
    shop: [
      { label: 'All Products', href: buildPath('/shop') },
      { label: 'Categories', href: buildPath('/categories') },
      { label: 'Featured', href: buildPath('/featured') },
      { label: 'New Arrivals', href: buildPath('/new') },
    ],
    account: [
      { label: 'My Account', href: buildPath('/account') },
      { label: 'Orders', href: buildPath('/orders') },
      { label: 'Wishlist', href: buildPath('/wishlist') },
      { label: 'Cart', href: buildPath('/cart') },
    ],
    support: [
      { label: 'Contact Us', href: buildPath('/contact') },
      { label: 'FAQs', href: buildPath('/faqs') },
      { label: 'Shipping Info', href: buildPath('/shipping') },
      { label: 'Returns', href: buildPath('/returns') },
    ],
    legal: [
      { label: 'Privacy Policy', href: buildPath('/privacy') },
      { label: 'Terms of Service', href: buildPath('/terms') },
      { label: 'Cookie Policy', href: buildPath('/cookies') },
    ],
  };

  return (
    <footer className="bg-gray-900 text-gray-300 overflow-x-hidden">
      <div className="max-w-[1920px] mx-auto px-2 sm:px-4 py-8 sm:py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <h3 className="text-2xl font-bold text-white mb-4">{config.storeName}</h3>
            <p className="text-gray-400 mb-6 max-w-md">
              Your trusted online store for quality products. We deliver excellence with every order.
            </p>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-hprimary-500" />
                <span>support@{config.storeName.toLowerCase().replace(/\s+/g, '')}.com</span>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-hprimary-500" />
                <span>+1 (555) 123-4567</span>
              </div>
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-hprimary-500" />
                <span>123 Store Street, City, Country</span>
              </div>
            </div>
          </div>

          {/* Shop Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Shop</h4>
            <ul className="space-y-2">
              {footerLinks.shop.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="hover:text-hprimary-500 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Account Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Account</h4>
            <ul className="space-y-2">
              {footerLinks.account.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="hover:text-hprimary-500 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Support</h4>
            <ul className="space-y-2">
              {footerLinks.support.map((link) => (
                <li key={link.href}>
                  <Link
                    href={link.href}
                    className="hover:text-hprimary-500 transition-colors"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-400 text-sm">
            © {new Date().getFullYear()} {config.storeName}. All rights reserved.
          </p>
          
          {/* Social Links */}
          <div className="flex items-center gap-4">
            <a
              href="#"
              className="w-10 h-10 bg-gray-800 hover:bg-hprimary-600 rounded-full flex items-center justify-center transition-colors"
            >
              <Facebook className="w-5 h-5" />
            </a>
            <a
              href="#"
              className="w-10 h-10 bg-gray-800 hover:bg-hprimary-600 rounded-full flex items-center justify-center transition-colors"
            >
              <Twitter className="w-5 h-5" />
            </a>
            <a
              href="#"
              className="w-10 h-10 bg-gray-800 hover:bg-hprimary-600 rounded-full flex items-center justify-center transition-colors"
            >
              <Instagram className="w-5 h-5" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

